import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  CreateSupplierMappingByEmailMutationRequest,
  CreateSupplierMappingByEmailMutationResponse,
} from "../types/CreateSupplierMappingByEmail"

type CreateSupplierMappingByEmailClient = typeof client<
  CreateSupplierMappingByEmailMutationResponse,
  Error,
  CreateSupplierMappingByEmailMutationRequest
>
type CreateSupplierMappingByEmail = {
  data: CreateSupplierMappingByEmailMutationResponse
  error: Error
  request: CreateSupplierMappingByEmailMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<CreateSupplierMappingByEmailClient>>
  client: {
    parameters: Partial<Parameters<CreateSupplierMappingByEmailClient>[0]>
    return: Awaited<ReturnType<CreateSupplierMappingByEmailClient>>
  }
}
/**
 * @summary Create a supplier mapping by email
 * @link /v1/client-settings/create-supplier-mapping-by-email
 */
export function useCreateSupplierMappingByEmail(
  options: {
    mutation?: UseMutationOptions<
      CreateSupplierMappingByEmail["response"],
      CreateSupplierMappingByEmail["error"],
      CreateSupplierMappingByEmail["request"]
    >
    client?: CreateSupplierMappingByEmail["client"]["parameters"]
  } = {}
): UseMutationResult<
  CreateSupplierMappingByEmail["response"],
  CreateSupplierMappingByEmail["error"],
  CreateSupplierMappingByEmail["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    CreateSupplierMappingByEmail["response"],
    CreateSupplierMappingByEmail["error"],
    CreateSupplierMappingByEmail["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        CreateSupplierMappingByEmail["data"],
        CreateSupplierMappingByEmail["error"],
        CreateSupplierMappingByEmail["request"]
      >({
        method: "post",
        url: `/v1/client-settings/create-supplier-mapping-by-email`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
